import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { helmetTypeApi } from '../api';
import type { HelmetTypeFormData, HelmetTypeListFilters } from '../types';

const KEYS = {
  all: ['helmetTypes'] as const,
  lists: () => [...KEYS.all, 'list'] as const,
  list: (filters: HelmetTypeListFilters) => [...KEYS.lists(), filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useHelmetTypes(filters: HelmetTypeListFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: async () => {
      const { data } = await helmetTypeApi.list(filters);
      return data;
    },
  });
}

export function useHelmetTypeDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await helmetTypeApi.getById(id!);
      return data.helmetType;
    },
    enabled: !!id,
  });
}

export function useCreateHelmetType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: HelmetTypeFormData) => {
      const { data } = await helmetTypeApi.create(formData);
      return data.helmetType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateHelmetType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<HelmetTypeFormData> }) => {
      const { data: response } = await helmetTypeApi.update(id, data);
      return response.helmetType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeactivateHelmetType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await helmetTypeApi.deactivate(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
